import clsx from 'clsx';
import { type ElementType, type ReactElement, forwardRef } from 'react';

import { text } from './Text.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { textStyle } from '../../styles/textStyle.sty.js';

const COMPONENT_NAME = 'Text';

/**
 * The props for the text component without any props coming from polymorphing.
 * @public
 */
export interface TextOwnProps
  extends WithChildren,
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /** Sets the text style. Variants include "base", "base-emphasized", etc. */
  textStyle?: 'base' | 'base-emphasized' | 'small' | 'small-emphasized';
  /** Sets the font style. Can either be "text" or "code" */
  fontStyle?: 'text' | 'code';
}

/**
 * Combined props for the text component - polymorphing and own props.
 * @public
 */
export type TextProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  TextOwnProps
>;

/**
 * Use the `Text` component for text that is rendered without any semantic markup.
 * @public
 */
export const Text: <E extends ElementType = 'span'>(
  props: TextProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    {
      children,
      textStyle: textStyleProp,
      fontStyle,
      as,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      ...remainingProps
    }: TextProps<E>,
    ref: typeof remainingProps.ref,
  ) => {
    const TextTag = as || 'span';

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    let ellipsis: undefined | 'singleLine' | 'multiLine';

    return (
      <TextTag
        ref={ref}
        className={clsx([
          text({ ellipsis }),
          (fontStyle || textStyleProp) &&
            textStyle({ fontStyle, textStyle: textStyleProp }),
          consumerClassName,
        ])}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-dt-component={dataDtComponent}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
      >
        {children}
      </TextTag>
    );
  },
);

(Text as typeof Text & { displayName: string }).displayName = COMPONENT_NAME;
